import { DefaultRootState } from '@databases/redux';
import {
  Database,
  DatabaseEntry,
  DatabaseField,
  DatabaseFieldType,
  DatabaseValue,
  ImportedFileType,
} from '@databases/types/database';
import { Cheerio } from 'cheerio';
import { Element, Node } from 'domhandler';

import {
  cheerioIterator,
  replaceBlankCharacters,
  getTextContent,
  parseHTML,
} from '../lib/dom';
import { parse } from '../shims/papaparse';
import { isFile } from '../utils';

import { convertType, getInitialValue, isEmptyValue, parseFromHtml } from './field-types';
import {
  createEntry,
  createField,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  getExistingFieldNames,
  getUniqueFieldName,
} from './utils';

// The process of importing a file is split up into multiple steps due to the complex interactions of the UI
// with different iframes:
// 1. To check whether a pasted list of files contains any files at all, use isImportedContentEmpty(). This way
//    the paste event can be ignored when an empty list is pasted.
// 2. Call importFileStep1() to read the content of the pasted file into a string. This needs to be done
//    synchronously on the paste event, as later the pasted files are not accessible anymore. Since the file
//    is read into a string, it can be passed to other frames without any problems.
// 3. Call importFileStep2() to parse the file read by step 1. This step performs any parsing that is unrelated
//    to the import settings, to make sure that this part of the parsing does not have to be repeated each time
//    the import settings are updated. For example for HTML files, this will parse the content into a DOM tree.
// 4. Call importFileStep3() to apply the import settings to the file parsed by step 2.

export type ImportedFileStep1 = {
  name: string;
  mimeType: string;
  rawContent: string;
  type: ImportedFileType;
};

export type ImportedFileStep2 = ImportedFileStep1 &
  (
    | {
        type: ImportedFileType.HTML;
        content: Array<Array<Cheerio<Element> | undefined>>;
      }
    | {
        type: ImportedFileType.CSV;
        content: string;
      }
  );

/**
 * The supported import MIME types, in order of preference (if content is pasted that has multiple MIME type representations,
 * the one first in this list is used).
 */
export const importMimeTypes: Record<string, ImportedFileType> = {
  'text/html': ImportedFileType.HTML,
  'text/csv': ImportedFileType.CSV,
  'application/vnd.ms-excel': ImportedFileType.CSV, // fixes a bug with systems running Firefox on Windows with Office installed, for more details see https://product-fabric.atlassian.net/browse/NCTD-1542
  'text/tab-separated-values': ImportedFileType.CSV,
  'text/plain': ImportedFileType.CSV,
};

/**
 * Returns true if the given list of pasted files is empty.
 */
export function isImportedContentEmpty(files: File[] | DataTransferItemList): boolean {
  return [...files].length === 0;
}

export enum ImportErrorTag {
  UNSUPPORTED_MIME_TYPE = 'UNSUPPORTED_MIME_TYPE',
  PASTED_FILE_CANNOT_BE_READ = 'PASTED_FILE_CANNOT_BE_READ',
  TABLE_COULD_NOT_BE_FOUND_IN_HTML = 'TABLE_COULD_NOT_BE_FOUND_IN_HTML',
  UNEXPECTED_DATA_TYPE = 'UNEXPECTED_DATA_TYPE',
  UNEXPECTED_IMPORT_TYPE = 'UNEXPECTED_IMPORT_TYPE',
  UNKNOWN_FILE_TYPE = 'UNKNOWN_FILE_TYPE',
}

/**
 * Takes a list of pasted files and loads the one that is the most suitable for a database import. Detects the file type
 * but reads the file into a string, without any parsing. If there is a problem with the picked file (for example is has
 * an unsupported MIME type), an error is thrown.
 * If the list of pasted files is empty, an error is thrown. This error can be prevented by checking the list of files
 * using isImportedContentEmpty() beforehand.
 */
export async function importFileStep1(files: File[] | DataTransferItemList): Promise<
  | { success: true; file: ImportedFileStep1 }
  | {
      success: false;
      error: { tag: ImportErrorTag.UNSUPPORTED_MIME_TYPE; fileType: string };
    }
  | { success: false; error: { tag: ImportErrorTag.PASTED_FILE_CANNOT_BE_READ } }
  | { success: false; error: { tag: ImportErrorTag.UNEXPECTED_DATA_TYPE } }
> {
  if (Array.isArray(files) && isFile(files[0])) {
    if (!importMimeTypes[files[0].type]) {
      return {
        success: false,
        error: {
          tag: ImportErrorTag.UNSUPPORTED_MIME_TYPE,
          fileType: files[0].type,
        },
      };
    }

    return {
      success: true,
      file: {
        name: files[0].name,
        type: importMimeTypes[files[0].type],
        mimeType: files[0].type,
        rawContent: await files[0].text(),
      },
    };
  } else if (files instanceof DataTransferItemList) {
    const items = [...files];

    const stringItems = items.filter((item) => item.kind === 'string');
    if (stringItems.length > 0) {
      for (const [mimeType, type] of Object.entries(importMimeTypes)) {
        const item = stringItems.find((item) => item.type === mimeType);
        if (item) {
          const rawContent = await new Promise<string>((resolve) => {
            item.getAsString(resolve);
          });
          if (!rawContent) {
            return {
              success: false,
              error: {
                tag: ImportErrorTag.PASTED_FILE_CANNOT_BE_READ,
              },
            };
          }
          return {
            success: true,
            file: {
              name: `Pasted content (${type})`,
              type,
              mimeType,
              rawContent,
            },
          };
        }
      }

      return {
        success: false,
        error: {
          tag: ImportErrorTag.UNSUPPORTED_MIME_TYPE,
          fileType: stringItems.map((item) => item.type).join(', '),
        },
      };
    }

    const fileItem = items.find((item) => item.kind === 'file');
    if (fileItem) {
      if (!importMimeTypes[fileItem.type]) {
        return {
          success: false,
          error: {
            tag: ImportErrorTag.UNSUPPORTED_MIME_TYPE,
            fileType: fileItem.type,
          },
        };
      }

      const file = fileItem.getAsFile();
      if (!file) {
        return {
          success: false,
          error: {
            tag: ImportErrorTag.PASTED_FILE_CANNOT_BE_READ,
          },
        };
      }
      return {
        success: true,
        file: {
          name: file.name,
          type: importMimeTypes[fileItem.type],
          mimeType: file.type,
          rawContent: await file.text(),
        },
      };
    }
  }

  return { success: false, error: { tag: ImportErrorTag.UNEXPECTED_DATA_TYPE } };
}

/**
 * Takes a pasted file loaded by importFileStep1() and parses its content. If there is a problem
 * with the picked file (for example the pasted HTML does not contain a table), an error is thrown.
 */
export async function importFileStep2(file: ImportedFileStep1): Promise<
  | { success: true; file: ImportedFileStep2 }
  | { success: false; error: { tag: ImportErrorTag.TABLE_COULD_NOT_BE_FOUND_IN_HTML } }
  | {
      success: false;
      error: { tag: ImportErrorTag.UNEXPECTED_IMPORT_TYPE; fileType: string };
    }
> {
  switch (file.type) {
    case ImportedFileType.HTML: {
      const parsed = parseHTML(file.rawContent);

      // Find a table in the content and extract its rows. The pasted content can contain a table element (with thead/tbody/tfoot children
      // or direct tr children), or it can be a list of tr elements (the wrapping table is not part of the content), or it can be a list
      // of th/td elements (in which case it represents a single table row).
      const table = parsed.find('table').eq(0);
      const rowEls = [
        ...cheerioIterator(
          table.length === 0
            ? parsed.find('tr')
            : table.find('> tr,> thead > tr,> tbody > tr,> tfoot > tr')
        ),
      ];
      const rows: Array<Array<Cheerio<Element> | undefined>> = (
        rowEls.length === 0
          ? [parsed.find('th,td')]
          : rowEls.map((row) => row.children('th,td'))
      )
        .map((row) => [...cheerioIterator(row)])
        .filter((row) => row.length > 0);
      if (rows.length === 0) {
        return {
          success: false,
          error: {
            tag: ImportErrorTag.TABLE_COULD_NOT_BE_FOUND_IN_HTML,
          },
        };
      }

      for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
        const row = rows[rowIdx];

        for (const col of row) {
          if (col) {
            const colIdx = row.indexOf(col);

            const colspan = parseInt(col.attr('colspan') ?? '1');
            if (colspan > 1) {
              row.splice(colIdx + 1, 0, ...new Array(colspan - 1));
            }

            const rowspan = parseInt(col.attr('rowspan') ?? '1');
            if (rowspan > 1) {
              for (let i = rowIdx + 1; i < rowIdx + rowspan; i++) {
                rows[i]?.splice(colIdx, 0, undefined);
              }
            }
          }
        }
      }

      return {
        success: true,
        file: {
          name: file.name,
          type: file.type,
          mimeType: file.mimeType,
          rawContent: file.rawContent,
          content: rows,
        },
      };
    }

    case ImportedFileType.CSV: {
      return {
        success: true,
        file: {
          name: file.name,
          type: file.type,
          mimeType: file.mimeType,
          rawContent: file.rawContent,
          content: file.rawContent,
        },
      };
    }

    default:
      return {
        success: false,
        error: { tag: ImportErrorTag.UNEXPECTED_IMPORT_TYPE, fileType: file.type },
      };
  }
}

export enum ImportFieldMode {
  EXISTING = 'existing',
  CREATE = 'create',
  IGNORE = 'ignore',
}

export type ImportFieldReference =
  | { type: ImportFieldMode.EXISTING; fieldId: string }
  | { type: ImportFieldMode.CREATE; field: DatabaseField }
  | { type: ImportFieldMode.IGNORE };

export function resolveFieldReference(
  database: Database,
  fieldRef: ImportFieldReference | undefined
): DatabaseField | undefined {
  if (fieldRef?.type === ImportFieldMode.EXISTING) {
    return getDatabaseFieldOptional(database, fieldRef.fieldId);
  } else if (fieldRef?.type === ImportFieldMode.CREATE) {
    return fieldRef.field;
  }
}

export enum ImportEntryMode {
  EXISTING = 'existing',
  CREATE = 'create',
}

export type ImportEntryReference =
  | { type: ImportEntryMode.EXISTING; entryId: string }
  | { type: ImportEntryMode.CREATE; entry: DatabaseEntry };

export function resolveEntryReference(
  database: Database,
  entryRef: ImportEntryReference | undefined
): DatabaseEntry | undefined {
  if (entryRef?.type === ImportEntryMode.EXISTING) {
    return getDatabaseEntryOptional(database, entryRef.entryId);
  } else if (entryRef?.type === ImportEntryMode.CREATE) {
    return entryRef.entry;
  }
}

export interface ImportSettings {
  /**
   * Which field to map each column to, in order.
   * A string references an existing database field by ID.
   * A DatabaseField object represents a database field that should be newly created.
   * null or undefined means that the field should be ignored.
   */
  fieldReferences: Array<ImportFieldReference | undefined>;
  /** For each row whether it should be imported or not. The default is true. */
  selectedRows: boolean[];

  /**
   * Which entry to map each row to, in order.
   * */
  entryReferences: Array<ImportEntryReference | undefined>;
  /**
   * For each column whether it should be imported or not. The default is true.
   * */
  selectedColumns: boolean[];
}

export interface ImportColumn {
  /** A text representation of the cell content. */
  text: string;
  /** The cell parsed as a database value. Undefined if no field is selected for this column. */
  value?: DatabaseValue;
  /**
   * Is true if the parsed database value is empty. The cause may be that the imported cell is empty or that it does not contain
   * any data that is compatible with the selected field type.
   */
  isEmpty: boolean;
}

export interface ImportResult {
  /** The number of columns in the imported data. */
  colCount: number;
  /** The parsed data rows and cells in the imported data. */
  rows: Array<{
    /** Whether the row is selected to be imported in the import settings. */
    isSelected: boolean;

    cols: Array<ImportColumn>;
  }>;
}

/**
 * Takes an imported file generated by importFileStep2() and parses it according to the specified import settings.
 */
export function importFileStep3(
  database: Database,
  file: ImportedFileStep2,
  settings: ImportSettings,
  state: DefaultRootState
):
  | { success: true; result: ImportResult }
  | {
      success: false;
      error: { tag: ImportErrorTag.UNKNOWN_FILE_TYPE; fileType: string };
    } {
  switch (file.type) {
    case ImportedFileType.HTML: {
      const cols = file.content.reduce((res, row) => Math.max(row.length, res), 0);
      const colsArr = [...new Array(cols)];
      const rows: ImportResult['rows'] = file.content.map((row, rowIdx) => {
        const entry = resolveEntryReference(database, settings.entryReferences[rowIdx]);
        return {
          isSelected: settings.selectedRows[rowIdx] ?? true,
          cols: colsArr.map((x, i) => {
            const cell = row[i];
            const field = resolveFieldReference(database, settings.fieldReferences[i]);
            const value = !field
              ? undefined
              : cell
              ? parseFromHtml(cell, { database, field, entry: entry! }, state)
              : getInitialValue(field.type);
            return {
              text: cell
                ? getTextContent(cell, (node: Node) =>
                    replaceBlankCharacters(node, false)
                  )
                : '',
              value,
              isEmpty: !value || isEmptyValue({ database, field: field!, value }, state),
            };
          }),
        };
      });
      return { success: true, result: { colCount: cols, rows } };
    }

    case ImportedFileType.CSV: {
      const data = parse<string[]>(file.content, { skipEmptyLines: true });
      const cols = data.data.reduce((res, row) => Math.max(row.length, res), 0);
      const colsArr = [...new Array(cols)];
      const rows: ImportResult['rows'] = data.data.map((row, rowIdx) => ({
        isSelected: settings.selectedRows[rowIdx] ?? true,
        cols: colsArr.map((x, i) => {
          const text = row[i] ?? '';
          const value: DatabaseValue = { type: DatabaseFieldType.TEXT, value: text };
          const field = resolveFieldReference(database, settings.fieldReferences[i]);
          const convertedValue = field
            ? convertType({ database, field, value }, field.type, state)
            : value;
          return {
            text,
            value,
            isEmpty: field
              ? isEmptyValue({ database, field, value: convertedValue }, state)
              : !text,
          };
        }),
      }));
      return { success: true, result: { colCount: cols, rows } };
    }

    default:
      return {
        success: false,
        error: { tag: ImportErrorTag.UNKNOWN_FILE_TYPE, fileType: (file as any).type },
      };
  }
}

/**
 * Determines the missing rows or columns based on the given all rows, selected rows and imported rows.
 * @param allItems - All rows or columns
 * @param selectedItems - Selected rows or columns
 * @param importedItems - Imported rows or columns
 * @returns the last 'n' entries of importedRows, where 'n' is the difference between the sum of the index and the length of importedRows,
 * and the length of allRows. Otherwise, returns an empty array.
 */
export const getMissingItems = <A>(
  allItems: string[],
  selectedItems: string[],
  importedItems: Array<A>
) => {
  const selectedIndex = allItems.indexOf(selectedItems[0]);

  const itemsToReturn = selectedIndex + importedItems.length - allItems.length;

  if (itemsToReturn > 0) {
    // Return the last items of the importedItems
    return importedItems.slice(-itemsToReturn);
  } else {
    return [];
  }
};

/**
 * Determines the import settings which contain entry references and field references with ImportEntryMode.EXISTING taking the
 * fieldIds and entryIds as input and starting from the selected indexes until the end of the fieldIds and entryIds.
 * @returns the import settings for the selected field and entry ids.
 */
export const getImportSettingsFromSelection = (
  selectedFieldIds: string[],
  selectedEntryIds: string[],
  fieldIds: string[],
  entryIds: string[]
): ImportSettings => ({
  fieldReferences: fieldIds
    .slice(fieldIds.indexOf(selectedFieldIds[0]))
    .map((fieldId) => ({
      type: ImportFieldMode.EXISTING,
      fieldId,
    })),
  entryReferences: entryIds
    .slice(entryIds.indexOf(selectedEntryIds[0]))
    .map((entryId) => ({
      type: ImportEntryMode.EXISTING,
      entryId,
    })),
  selectedRows: selectedEntryIds.map(() => true),
  selectedColumns: selectedFieldIds.map(() => true),
});

/**
 * Determines the import settings which contain entry references and field references with ImportEntryMode.CREATE and
 * ImportFieldMode.CREATE. Takes the settings for the selection and appends the missing fields and entries to the fieldReferences and
 * entryReferences. New fields are created with a unique name.
 * @returns the import settings for the selected field and entry ids as well as the missing fields and entries.
 */
export const getImportSettingsWithMissingItems = (
  selectedFieldIds: string[],
  selectedEntryIds: string[],
  fieldIds: string[],
  entryIds: string[],
  database: Database,
  importResult: ImportResult,
  fieldLabel: string
): ImportSettings => {
  const settings = getImportSettingsFromSelection(
    selectedFieldIds,
    selectedEntryIds,
    fieldIds,
    entryIds
  );
  const missingEntries = getMissingItems(entryIds, selectedEntryIds, importResult.rows);
  const missingFields = getMissingItems(
    fieldIds,
    selectedFieldIds,
    importResult.rows[0].cols
  );
  const addedFieldNames: string[] = [];
  return {
    ...settings,
    fieldReferences: [
      ...settings.fieldReferences,
      ...missingFields.map(() => {
        const uniqueFieldName = getUniqueFieldName(fieldLabel, [
          ...getExistingFieldNames(database),
          ...addedFieldNames,
        ]);
        addedFieldNames.push(uniqueFieldName);
        return {
          type: ImportEntryMode.CREATE,
          field: {
            ...createField(DatabaseFieldType.TEXT),
            name: uniqueFieldName,
          },
        };
      }),
    ] as ImportFieldReference[],
    entryReferences: [
      ...settings.entryReferences,
      ...missingEntries.map(() => ({
        type: ImportFieldMode.CREATE,
        entry: createEntry(database),
      })),
    ] as ImportEntryReference[],
  };
};
